import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle, Circle, Sparkle, FilePdf, CaretDown, CaretRight } from "@phosphor-icons/react";
import SnapshotDialog from "@/components/SnapshotDialog";
import CoachPanel from "@/components/playbook/CoachPanel";
import PlaybookProposalsPanel from "@/components/PlaybookProposalsPanel";
import { PLAYBOOKS, ensureShape, fetchPlaybook } from "@/components/playbook/prompts";

// Iter 85.35 · Per-engagement collapse state key. Persisted in localStorage
// so a collapsed Playbook stays collapsed while the advisor navigates
// within the same Engagement (or comes back later), without being a global
// setting that would force every Playbook in the firm into the same state.
const COLLAPSE_KEY = (eid) => `yadid.playbookCollapsed.${eid}`;

export default function ConveningPlaybook({ engagement, onSaved }) {
  const type = engagement?.engagement_type;
  const [template, setTemplate] = useState(() => PLAYBOOKS[type] || null);
  const stored = engagement?.playbook || engagement?.convening_playbook;
  const [book, setBook] = useState(() => (template ? ensureShape(stored, template.sections) : {}));
  const [saving, setSaving] = useState(false);
  const [openSection, setOpenSection] = useState(template?.sections?.[0]?.key || "");
  const [coachLoading, setCoachLoading] = useState(false);
  const [coach, setCoach] = useState(engagement?.last_coach_suggestion || null);
  const [focusKey, setFocusKey] = useState(null);
  const [snapshotOpen, setSnapshotOpen] = useState(false);
  // Iter 85.35 · Collapse/expand for the entire Playbook block.
  // Initialised from localStorage so navigating away and back within the
  // same Engagement preserves the advisor's choice. Default expanded.
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    try { return localStorage.getItem(COLLAPSE_KEY(engagement?.id)) === "1"; }
    catch { return false; }
  });
  useEffect(() => {
    if (typeof window === "undefined" || !engagement?.id) return;
    try { localStorage.setItem(COLLAPSE_KEY(engagement.id), collapsed ? "1" : "0"); }
    catch {}
  }, [collapsed, engagement?.id]);
  // If the engagement changes (advisor navigated to a different one),
  // re-read the persisted state for that engagement so each carries its
  // own collapsed/expanded memory.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try { setCollapsed(localStorage.getItem(COLLAPSE_KEY(engagement?.id)) === "1"); }
    catch {}
  }, [engagement?.id]);

  // Load the active (possibly customised) playbook from the DB so partner-edited
  // prompts in Settings show up here without a static-file redeploy.
  useEffect(() => {
    let cancelled = false;
    if (!type) return () => { cancelled = true; };
    fetchPlaybook(type).then((fresh) => {
      if (cancelled || !fresh) return;
      setTemplate(fresh);
      setBook(ensureShape(stored, fresh.sections));
      setOpenSection((prev) => prev || fresh.sections?.[0]?.key || "");
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, engagement?.id]);

  const focusItem = (section, idx) => {
    if (section == null || idx == null) return;
    setOpenSection(section);
    setTimeout(() => {
      const el = document.querySelector(`[data-testid='playbook-item-${section}-${idx}']`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      const key = `${section}-${idx}`;
      setFocusKey(key);
      setTimeout(() => setFocusKey((k) => (k === key ? null : k)), 2400);
    }, 60);
  };

  const completion = useMemo(() => {
    const all = Object.values(book).flat();
    const done = all.filter((i) => i.done).length;
    return { done, total: all.length, pct: all.length ? Math.round((done / all.length) * 100) : 0 };
  }, [book]);

  if (!template) return null;

  const updateItem = (section, idx, patch) => {
    setBook({ ...book, [section]: book[section].map((it, i) => (i === idx ? { ...it, ...patch } : it)) });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/engagements/${engagement.id}`, { playbook: book });
      toast.success("Playbook saved");
      if (onSaved) onSaved(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const askCoach = async () => {
    setCoachLoading(true);
    setCoach(null);
    try {
      await api.put(`/engagements/${engagement.id}/`, { playbook: book }).catch(() => {});
      const { data } = await api.post(`/engagements/${engagement.id}/playbook-coach`);
      setCoach(data);
      if (data?.suggested_section) setOpenSection(data.suggested_section);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Coach unavailable");
    } finally {
      setCoachLoading(false);
    }
  };

  const sectionCount = template.sections.length;
  const gridCols = sectionCount === 3 ? "md:grid-cols-3" : sectionCount === 4 ? "md:grid-cols-4" : "md:grid-cols-2";

  return (
    <div className="border border-primary/40 bg-card mb-6" data-testid="convening-playbook">
      {/*
        Iter 85.35 · Header is always visible (title · blurb · progress) so
        even a collapsed Playbook keeps the advisor oriented. The chevron
        button toggles the entire detail block below. `aria-expanded` and
        `aria-controls` wire the disclosure semantics to the collapsible
        region so screen readers announce the state change.
      */}
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        aria-expanded={!collapsed}
        aria-controls="playbook-detail-region"
        data-testid="playbook-collapse-toggle"
        className="w-full flex items-center justify-between px-6 py-4 border-b border-border text-left hover:bg-secondary/30 transition-colors"
      >
        <div className="flex items-center gap-3 min-w-0">
          {collapsed
            ? <CaretRight size={14} weight="bold" className="text-primary shrink-0" />
            : <CaretDown size={14} weight="bold" className="text-primary shrink-0" />}
          <div className="min-w-0">
            <div className="text-[0.625rem] font-mono uppercase tracking-[0.2em] text-eyebrow">{template.label}</div>
            <div className="text-sm text-muted-foreground mt-0.5 truncate">{template.blurb}</div>
          </div>
        </div>
        <div className="text-right shrink-0 ml-4">
          <div className="font-display text-lg font-bold text-primary" data-testid="playbook-completion">{completion.done}/{completion.total}</div>
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">{completion.pct}% complete</div>
        </div>
      </button>

      {/*
        Iter 85.35 · Collapsible detail region — Proposals panel, section
        grid, orphan block, action bar, and Coach panel all live inside
        this gate. When `collapsed` is true, the region is unmounted so
        heavy children (e.g. PlaybookProposalsPanel's fetches) don't
        run — but ALL state (book, focus, coach, openSection) lives on the
        parent hook so nothing is lost. Re-expanding restores exactly the
        prior interaction state including per-section open state, notes,
        completion, additional playbook items, and coach output.
      */}
      {!collapsed && (
      <div id="playbook-detail-region">
      <div className="px-6 pt-6">
        <PlaybookProposalsPanel
          engagement={engagement}
          onApplied={async () => {
            // Refresh the parent engagement so newly-accepted playbook prompts
            // appear as fresh items in their sections without a manual reload.
            try {
              const { data } = await api.get(`/engagements/${engagement.id}`);
              onSaved?.(data);
              setBook(ensureShape(data.playbook || data.convening_playbook, template.sections));
            } catch {}
          }}
        />
      </div>

      <div className={`grid grid-cols-1 ${gridCols} divide-x divide-border`}>
        {template.sections.map((s) => {
          const Icon = s.icon;
          const items = book[s.key] || [];
          const doneCount = items.filter((i) => i.done).length;
          const open = openSection === s.key;
          return (
            <div key={s.key} className="p-5" data-testid={`playbook-section-${s.key}`}>
              <button
                onClick={() => setOpenSection(open ? "" : s.key)}
                className="w-full text-left"
                data-testid={`playbook-toggle-${s.key}`}
              >
                <div className="flex items-center gap-2 text-primary">
                  <Icon size={16} weight="bold" />
                  <div className="text-[0.625rem] font-mono uppercase tracking-widest">{s.label}</div>
                </div>
                <div className="text-[0.6875rem] text-muted-foreground mt-1">{s.blurb}</div>
                <div className="text-[0.625rem] font-mono text-primary/70 mt-1">{doneCount}/{items.length}</div>
              </button>
              {open && (
                <div className="mt-4 space-y-3">
                  {items.map((it, idx) => {
                    const isFocus = focusKey === `${s.key}-${idx}`;
                    return (
                      <div
                        key={idx}
                        className={`border-l-2 pl-3 transition-all duration-500 ${isFocus ? "border-primary bg-primary/10 ring-2 ring-primary" : "border-primary/30"}`}
                      >
                        <button
                          onClick={() => updateItem(s.key, idx, { done: !it.done })}
                          className="flex items-start gap-2 text-left w-full"
                          data-testid={`playbook-item-${s.key}-${idx}`}
                        >
                          {it.done
                            ? <CheckCircle size={16} weight="fill" className="text-primary shrink-0 mt-0.5" />
                            : <Circle size={16} className="text-muted-foreground shrink-0 mt-0.5" />}
                          <span className={`text-xs ${it.done ? "line-through text-muted-foreground" : "text-foreground"}`}>{it.prompt}</span>
                        </button>
                        <Textarea
                          rows={2}
                          value={it.note}
                          onChange={(e) => updateItem(s.key, idx, { note: e.target.value })}
                          placeholder="Notes, names, dates…"
                          className="rounded-none bg-secondary/40 mt-2 text-xs"
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/*
        Iter 85.32 · Orphan sections — playbook items applied from a
        Briefing whose section_key doesn't match this engagement_type's
        canonical template are surfaced here so no accepted proposal is
        silently dropped from the UI. Typical cause: the AI mapped the
        proposal to an executive_convening section (e.g. agenda_arc)
        while the engagement was created as a strategic_initiative.
      */}
      {(() => {
        const orphanKeys = Object.keys(book).filter((k) => !template.sections.some((s) => s.key === k));
        if (orphanKeys.length === 0) return null;
        const humanize = (k) => k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
        return (
          <div className="border-t border-border px-6 py-5" data-testid="playbook-orphans">
            <div className="flex items-center gap-2 text-primary mb-1">
              <Sparkle size={14} weight="bold" />
              <div className="text-[0.625rem] font-mono uppercase tracking-widest">Additional playbook items</div>
            </div>
            <div className="text-[0.6875rem] text-muted-foreground mb-4">
              Applied from a briefing on a section that isn't part of this engagement type's default template. They still count toward completion.
            </div>
            <div className="space-y-4">
              {orphanKeys.map((key) => {
                const items = book[key] || [];
                if (items.length === 0) return null;
                return (
                  <div key={key} data-testid={`playbook-orphan-section-${key}`}>
                    <div className="text-[0.625rem] font-mono uppercase tracking-widest text-eyebrow mb-2">{humanize(key)}</div>
                    <div className="space-y-2">
                      {items.map((it, idx) => (
                        <div key={idx} className="border-l-2 border-primary/30 pl-3">
                          <button
                            onClick={() => updateItem(key, idx, { done: !it.done })}
                            className="flex items-start gap-2 text-left w-full"
                            data-testid={`playbook-item-${key}-${idx}`}
                          >
                            {it.done
                              ? <CheckCircle size={16} weight="fill" className="text-primary shrink-0 mt-0.5" />
                              : <Circle size={16} className="text-muted-foreground shrink-0 mt-0.5" />}
                            <span className={`text-xs ${it.done ? "line-through text-muted-foreground" : "text-foreground"}`}>{it.prompt}</span>
                          </button>
                          <Textarea
                            rows={2}
                            value={it.note}
                            onChange={(e) => updateItem(key, idx, { note: e.target.value })}
                            placeholder="Notes, names, dates…"
                            className="rounded-none bg-secondary/40 mt-2 text-xs"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      <div className="flex flex-wrap items-center justify-end gap-2 px-6 py-3 border-t border-border">
        <button
          onClick={() => setSnapshotOpen(true)}
          data-testid="snapshot-open-btn"
          className="text-[0.6875rem] font-mono uppercase tracking-widest border border-border text-muted-foreground px-4 py-2 hover:text-eyebrow hover:border-primary/60 transition inline-flex items-center gap-2"
        >
          <FilePdf size={12} weight="bold" /> Export snapshot
        </button>
        <button
          onClick={askCoach}
          disabled={coachLoading}
          data-testid="playbook-coach-btn"
          className="text-[0.6875rem] font-mono uppercase tracking-widest border border-primary/60 text-primary px-4 py-2 hover:bg-primary/10 disabled:opacity-60 inline-flex items-center gap-2"
        >
          <Sparkle size={12} weight="fill" /> {coachLoading ? "Asking YadidAi…" : "Ask YadidAi: next move"}
        </button>
        <button
          onClick={save}
          disabled={saving}
          data-testid="playbook-save"
          className="text-[0.6875rem] font-mono uppercase tracking-widest bg-primary text-primary-foreground px-4 py-2 hover:bg-primary/90 disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save playbook"}
        </button>
      </div>

      <CoachPanel coach={coach} onFocus={focusItem} />

      <SnapshotDialog open={snapshotOpen} onClose={() => setSnapshotOpen(false)} engagement={engagement} />
      </div>
      )}
    </div>
  );
}
